import { useNavigate } from "react-router-dom";

const headers = [
    { key: "adversario", label: "Club", align: "text-left", isSortable: true },
    { key: "pais", label: "País", align: "text-left", isSortable: true },
    { key: "jugados", label: "PJ", align: "text-center", isSortable: true, colClass: "w-14 sm:w-20" },
    { key: "ganados", label: "PG", align: "text-center", isSortable: true, colClass: "w-14 sm:w-20" },
    { key: "perdidos", label: "PP", align: "text-center", isSortable: true, colClass: "w-14 sm:w-20" },
    { key: "diff", label: "Dif.", align: "text-center", isSortable: true, colClass: "w-14 sm:w-20" },
    { key: "winRate", label: "%", mobileLabel: "%", align: "text-right", isSortable: true, colClass: "w-14 sm:w-20" },
];

function SortIndicatorIcon({ isActive, sortDirection }) {
    const isAscending = sortDirection === "asc";

    return (
        <span className={`inline-flex h-4 w-4 items-center justify-center ${isActive ? "text-green-400" : "text-slate-500"}`} aria-hidden="true">
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
                {isActive ? (
                    isAscending ? (
                        <path d="M10 4L5 8h10L10 4Z" />
                    ) : (
                        <path d="M10 16l5-4H5l5 4Z" />
                    )
                ) : (
                    <>
                        <path d="M10 4L5 8h10L10 4Z" />
                        <path d="M10 16l5-4H5l5 4Z" />
                    </>
                )}
            </svg>
        </span>
    );
}

function HistorialTable({ rows, sortBy, sortDirection, onSortChange }) {
    const navigate = useNavigate();
    if (!rows.length) {
        return (
            <div className="rounded-xl border border-white/10 bg-[#102216] p-8 text-center text-slate-300 font-medium">
                No hay datos de historial para mostrar.
            </div>
        );
    }

    return (
        <div className="bg-[#102216] rounded-xl border border-white/10 shadow-2xl overflow-hidden">
            <p className="sm:hidden px-3 pt-3 pb-3 text-[11px] text-slate-400 uppercase tracking-[0.16em] font-bold">
                Deslizá para ver todas las columnas.
            </p>

            <div className="overflow-auto max-h-[calc(100vh-320px)] min-h-[300px]">
                <table className="w-full text-left border-collapse table-fixed sm:table-auto min-w-[520px] sm:min-w-[680px]">
                    <thead>
                        <tr className="bg-white/5 border-b border-white/10">
                            {headers.map((header) => {
                                const isPaisColumn = header.key === "pais";
                                const isClubColumn = header.key === "adversario";

                                return (
                                    <th
                                        key={header.key}
                                        className={`px-3 sm:px-6 py-3 sm:py-4 text-[11px] sm:text-xs font-bold text-slate-400 uppercase tracking-wider sticky top-0 z-20 bg-[#102216] ${header.align} ${header.colClass || ""} ${isPaisColumn ? "hidden sm:table-cell" : ""} ${isClubColumn ? "w-[28%] sm:w-[30%] left-0 z-30" : ""}`}
                                    >
                                        {header.isSortable ? (
                                            <button
                                                type="button"
                                                onClick={() => onSortChange(header.key)}
                                                className={`inline-flex items-center gap-1 hover:text-green-400 transition-colors ${header.align === "text-right" ? "ml-auto" : ""}`}
                                            >
                                                    <span>
                                                        {header.mobileLabel ? (
                                                            <>
                                                                <span className="sm:hidden">{header.mobileLabel}</span>
                                                                <span className="hidden sm:inline">{header.label}</span>
                                                            </>
                                                        ) : header.label}
                                                    </span>
                                                <SortIndicatorIcon isActive={sortBy === header.key} sortDirection={sortDirection} />
                                            </button>
                                        ) : header.label}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>

                    <tbody className="divide-y divide-white/10">
                        {rows.map((row) => (
                            <tr key={row.adversario} className="group hover:bg-white/[0.03] transition-colors">
                                <td className="w-[34%] sm:w-[30%] px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-bold text-slate-100 sticky left-0 z-20 bg-[#102216] shadow-[8px_0_10px_-10px_rgba(0,0,0,0.9)] group-hover:bg-[#183325] transition-colors">
                                    <button
                                        type="button"
                                        onClick={() => navigate(`/partidos/buscarpartido?adversario=${encodeURIComponent(row.adversario)}`)}
                                        className="leading-tight truncate text-left hover:text-green-400 transition-colors w-full"
                                    >
                                        {row.adversario}
                                    </button>
                                    <p className="text-[11px] text-slate-400 font-medium sm:hidden mt-0.5">{row.pais ?? "—"}</p>
                                </td>
                                <td className="hidden sm:table-cell px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-slate-300">{row.pais ?? "—"}</td>
                                <td className="w-14 sm:w-20 px-3 sm:px-6 py-3 sm:py-4 text-center font-mono text-xs sm:text-sm text-slate-300">{row.jugados}</td>
                                <td className="w-14 sm:w-20 px-3 sm:px-6 py-3 sm:py-4 text-center font-mono text-xs sm:text-sm text-green-400 font-bold">{row.ganados}</td>
                                <td className="w-14 sm:w-20 px-3 sm:px-6 py-3 sm:py-4 text-center font-mono text-xs sm:text-sm text-red-400 font-bold">{row.perdidos}</td>
                                <td className={`w-14 sm:w-20 px-3 sm:px-6 py-3 sm:py-4 text-center font-mono text-xs sm:text-sm font-bold ${row.diff > 0 ? "text-green-400" : row.diff < 0 ? "text-red-400" : "text-slate-400"}`}>
                                    {row.diff > 0 ? `+${row.diff}` : row.diff}
                                </td>
                                <td className="w-14 sm:w-20 px-3 sm:px-6 py-3 sm:py-4 text-right font-mono text-xs sm:text-sm text-slate-300">
                                    {row.winRate.toFixed(1)}%
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default HistorialTable;
